// @flow
import React, { useRef, useCallback, useMemo, type Node } from 'react';
import classNames from 'classnames/bind';
import keys from 'ramda/es/keys';
import join from 'ramda/es/join';
import values from 'ramda/es/values';
import take from 'ramda/es/take';
import { Trans, useTranslation } from 'react-i18next';

import PlainFile from 'app/common/ui/PlainFile';
import config from 'app/config';
import PlainButtonAsLink from 'app/common/ui-next/plain-button-as-link';

import styles from './Uploader.scss';


const cx = classNames.bind(styles);

const supportedAttachments = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'application/vnd.ms-excel': 'xls',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
  'application/msword': 'doc',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
  'application/pdf': 'pdf',
};

const acceptString = join(', ', keys(supportedAttachments)); // в компонент
const acceptFormats = join(', ', values(supportedAttachments)); // только отображение для пользователя

type Props = {
  className?: string,
  leftFilesCount: number,
  maxFilesAvailable: number,
  onAddFiles: (accepted: Array<File>, rejected: Array<File>) => void,
  disabled?: boolean,
}

const Uploader = ({ className, leftFilesCount, maxFilesAvailable, onAddFiles, disabled }: Props): Node => {
  const dropzone = useRef(null);
  const { t } = useTranslation('dnevnik');

  const handleOpenClick = useCallback(() => {
    if (dropzone.current && !disabled) {
      dropzone.current.open();
    }
  }, [disabled]);

  const handleAddFiles = useCallback((accepted, rejected) => {
    if (disabled) return;
    if (!leftFilesCount || leftFilesCount < 0) return;

    const acceptedWithLimit = take(leftFilesCount, accepted);
    const rejectedWithLimit = acceptedWithLimit.length < leftFilesCount
      ? take(leftFilesCount - acceptedWithLimit.length, rejected)
      : [];
    onAddFiles(acceptedWithLimit, rejectedWithLimit);
  }, [leftFilesCount, onAddFiles, disabled]);

  const comment = useMemo(() => {
    const commentString = t('dnevnik_solution_modal_dropzone_comment', {
      formats: acceptFormats,
      size: Math.round(config.maxFileSize / 1050578),
      count: maxFilesAvailable,
    });
    return commentString;
  }, [t, maxFilesAvailable]);

  return (
    <div className={className}>
      <PlainFile
        id="files"
        ref={dropzone}
        accept={acceptString}
        maxSize={config.maxFileSize}
        onDrop={handleAddFiles}
        comment={comment}
        disabled={disabled}
        disablePreview
      >
        {({ isDragActive, isDragReject }) =>
          <div className={cx(styles.dropArea, { isDragActive, isDragReject, disabled })}>
            <div className={styles.dropText}>
              <Trans i18nKey="dnevnik:dnevnik_solution_modal_drop_area">
                <PlainButtonAsLink
                  className={styles.selectButton}
                  onClick={handleOpenClick}
                  disabled={disabled}
                >
                  {'Выберите файл(ы)'}
                </PlainButtonAsLink>
                &nbsp;
                <span className={styles.text}>{'&nbsp;на компьютере'}</span>
                <span className={styles.text}>{'или перетащите их в это поле'}</span>
              </Trans>
            </div>
          </div>}
      </PlainFile>
    </div>
  );
};

export default Uploader;
